/**
 * Word document helpers for navigating to quoted evidence in checklist results.
 */

import type { SectionMatchType } from '../types';

const WORD_SEARCH_MAX_LENGTH = 255;

function contentControlMatchesTitle(
  title: string,
  sectionMatch: string,
  sectionMatchType: SectionMatchType,
): boolean {
  if (sectionMatchType === 'regex') {
    try {
      return new RegExp(sectionMatch, 'i').test(title);
    } catch {
      return false;
    }
  }
  return title.toLowerCase() === sectionMatch.toLowerCase();
}

function searchTextForWord(quote: string): string {
  const trimmed = quote.trim();
  if (trimmed.length <= WORD_SEARCH_MAX_LENGTH) {
    return trimmed;
  }
  return trimmed.slice(0, WORD_SEARCH_MAX_LENGTH);
}

/**
 * Find an exact quote in the document (or matching section) and select it so
 * Word scrolls the match into view.
 */
export async function highlightEvidenceQuote(
  quote: string,
  sectionMatch: string | null,
  sectionMatchType: SectionMatchType,
): Promise<void> {
  if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
    return;
  }

  const searchText = searchTextForWord(quote);
  if (!searchText) {
    return;
  }

  await Word.run(async (context) => {
    let searchScope: Word.Range | Word.Body = context.document.body;

    if (sectionMatchType !== 'document' && sectionMatch) {
      const contentControls = context.document.contentControls;
      contentControls.load('title');
      await context.sync();

      const match = contentControls.items.find((cc) => {
        const title = cc.title?.trim() ?? '';
        return contentControlMatchesTitle(title, sectionMatch, sectionMatchType);
      });

      if (match) {
        searchScope = match.getRange();
      }
    }

    const results = searchScope.search(searchText, {
      matchCase: false,
      matchWholeWord: false,
      matchWildcards: false,
    });
    results.load('items');
    await context.sync();

    if (results.items.length > 0) {
      results.items[0].select();
      await context.sync();
    }
  });
}
